import { tokens, type DesignTokens } from "./tokens"

export interface ByfolioTheme {
  readonly name: "byfolio"
  readonly tokens: DesignTokens
}

export const byfolioTheme: ByfolioTheme = {
  name: "byfolio",
  tokens,
}

export const cssVariables = {
  color: {
    white: "--byfolio-color-white",
    black: "--byfolio-color-black",
    night: "--byfolio-color-night",
    ink: "--byfolio-color-ink",
    charcoal: "--byfolio-color-charcoal",
    navy: "--byfolio-color-navy",
    navyDeep: "--byfolio-color-navy-deep",
    slate: "--byfolio-color-slate",
    blue: "--byfolio-color-blue",
    sky: "--byfolio-color-sky",
    skyBright: "--byfolio-color-sky-bright",
    ice: "--byfolio-color-ice",
    snow: "--byfolio-color-snow",
    cloud: "--byfolio-color-cloud",
    frost: "--byfolio-color-frost",
    skyPale: "--byfolio-color-sky-pale",
    violet: "--byfolio-color-violet",
    teal: "--byfolio-color-teal",
    lavender: "--byfolio-color-lavender",
    magenta: "--byfolio-color-magenta",
    red: "--byfolio-color-red",
    success: "--byfolio-color-success",
    danger: "--byfolio-color-danger",
  },
  semanticColor: {
    canvas: "--byfolio-canvas",
    surface: "--byfolio-surface",
    surfaceElevated: "--byfolio-surface-elevated",
    surfaceGlass: "--byfolio-surface-glass",
    control: "--byfolio-control",
    controlFocus: "--byfolio-control-focus",
    text: "--byfolio-text",
    textMuted: "--byfolio-text-muted",
    textSubtle: "--byfolio-text-subtle",
    fieldLabel: "--byfolio-field-label",
    placeholder: "--byfolio-placeholder",
    accent: "--byfolio-accent",
    accentStrong: "--byfolio-accent-strong",
    focus: "--byfolio-focus",
    border: "--byfolio-border",
    borderStrong: "--byfolio-border-strong",
    success: "--byfolio-success",
    danger: "--byfolio-danger",
  },
  space: {
    none: "--byfolio-space-none",
    px: "--byfolio-space-px",
    xs: "--byfolio-space-xs",
    sm: "--byfolio-space-sm",
    md: "--byfolio-space-md",
    lg: "--byfolio-space-lg",
    xl: "--byfolio-space-xl",
    "2xl": "--byfolio-space-2xl",
    "3xl": "--byfolio-space-3xl",
    "4xl": "--byfolio-space-4xl",
    "5xl": "--byfolio-space-5xl",
  },
  size: {
    content: "--byfolio-size-content",
    contentWide: "--byfolio-size-content-wide",
    controlSmall: "--byfolio-size-control-small",
    control: "--byfolio-size-control",
    controlLarge: "--byfolio-size-control-large",
  },
  radius: {
    none: "--byfolio-radius-none",
    sm: "--byfolio-radius-sm",
    md: "--byfolio-radius-md",
    lg: "--byfolio-radius-lg",
    xl: "--byfolio-radius-xl",
    round: "--byfolio-radius-round",
  },
  font: {
    body: "--byfolio-font-body",
    mono: "--byfolio-font-mono",
  },
  fontSize: {
    xs: "--byfolio-font-size-xs",
    sm: "--byfolio-font-size-sm",
    md: "--byfolio-font-size-md",
    lg: "--byfolio-font-size-lg",
    xl: "--byfolio-font-size-xl",
    "2xl": "--byfolio-font-size-2xl",
    "3xl": "--byfolio-font-size-3xl",
    "4xl": "--byfolio-font-size-4xl",
  },
  fontWeight: {
    regular: "--byfolio-font-weight-regular",
    medium: "--byfolio-font-weight-medium",
    bold: "--byfolio-font-weight-bold",
    black: "--byfolio-font-weight-black",
  },
  lineHeight: {
    tight: "--byfolio-line-height-tight",
    heading: "--byfolio-line-height-heading",
    compact: "--byfolio-line-height-compact",
    body: "--byfolio-line-height-body",
  },
  shadow: {
    sm: "--byfolio-shadow-sm",
    md: "--byfolio-shadow-md",
    lg: "--byfolio-shadow-lg",
    focus: "--byfolio-shadow-focus",
  },
  duration: {
    instant: "--byfolio-duration-instant",
    fast: "--byfolio-duration-fast",
    normal: "--byfolio-duration-normal",
    page: "--byfolio-duration-page",
  },
  easing: {
    standard: "--byfolio-easing-standard",
    entrance: "--byfolio-easing-entrance",
  },
} as const

type CssVariableMap = Readonly<Record<`--byfolio-${string}`, string>>

export const createCssVariableMap = (theme: ByfolioTheme): CssVariableMap => {
  const value = theme.tokens

  return {
    [cssVariables.color.white]: value.color.white,
    [cssVariables.color.black]: value.color.black,
    [cssVariables.color.night]: value.color.night,
    [cssVariables.color.ink]: value.color.ink,
    [cssVariables.color.charcoal]: value.color.charcoal,
    [cssVariables.color.navy]: value.color.navy,
    [cssVariables.color.navyDeep]: value.color.navyDeep,
    [cssVariables.color.slate]: value.color.slate,
    [cssVariables.color.blue]: value.color.blue,
    [cssVariables.color.sky]: value.color.sky,
    [cssVariables.color.skyBright]: value.color.skyBright,
    [cssVariables.color.ice]: value.color.ice,
    [cssVariables.color.snow]: value.color.snow,
    [cssVariables.color.cloud]: value.color.cloud,
    [cssVariables.color.frost]: value.color.frost,
    [cssVariables.color.skyPale]: value.color.skyPale,
    [cssVariables.color.violet]: value.color.violet,
    [cssVariables.color.teal]: value.color.teal,
    [cssVariables.color.lavender]: value.color.lavender,
    [cssVariables.color.magenta]: value.color.magenta,
    [cssVariables.color.red]: value.color.red,
    [cssVariables.color.success]: value.color.success,
    [cssVariables.color.danger]: value.color.danger,
    [cssVariables.semanticColor.canvas]: value.semanticColor.canvas,
    [cssVariables.semanticColor.surface]: value.semanticColor.surface,
    [cssVariables.semanticColor.surfaceElevated]:
      value.semanticColor.surfaceElevated,
    [cssVariables.semanticColor.surfaceGlass]: value.semanticColor.surfaceGlass,
    [cssVariables.semanticColor.control]: value.semanticColor.control,
    [cssVariables.semanticColor.controlFocus]: value.semanticColor.controlFocus,
    [cssVariables.semanticColor.text]: value.semanticColor.text,
    [cssVariables.semanticColor.textMuted]: value.semanticColor.textMuted,
    [cssVariables.semanticColor.textSubtle]: value.semanticColor.textSubtle,
    [cssVariables.semanticColor.fieldLabel]: value.semanticColor.fieldLabel,
    [cssVariables.semanticColor.placeholder]: value.semanticColor.placeholder,
    [cssVariables.semanticColor.accent]: value.semanticColor.accent,
    [cssVariables.semanticColor.accentStrong]: value.semanticColor.accentStrong,
    [cssVariables.semanticColor.focus]: value.semanticColor.focus,
    [cssVariables.semanticColor.border]: value.semanticColor.border,
    [cssVariables.semanticColor.borderStrong]: value.semanticColor.borderStrong,
    [cssVariables.semanticColor.success]: value.semanticColor.success,
    [cssVariables.semanticColor.danger]: value.semanticColor.danger,
    [cssVariables.space.none]: value.space.none,
    [cssVariables.space.px]: value.space.px,
    [cssVariables.space.xs]: value.space.xs,
    [cssVariables.space.sm]: value.space.sm,
    [cssVariables.space.md]: value.space.md,
    [cssVariables.space.lg]: value.space.lg,
    [cssVariables.space.xl]: value.space.xl,
    [cssVariables.space["2xl"]]: value.space["2xl"],
    [cssVariables.space["3xl"]]: value.space["3xl"],
    [cssVariables.space["4xl"]]: value.space["4xl"],
    [cssVariables.space["5xl"]]: value.space["5xl"],
    [cssVariables.size.content]: value.size.content,
    [cssVariables.size.contentWide]: value.size.contentWide,
    [cssVariables.size.controlSmall]: value.size.controlSmall,
    [cssVariables.size.control]: value.size.control,
    [cssVariables.size.controlLarge]: value.size.controlLarge,
    [cssVariables.radius.none]: value.radius.none,
    [cssVariables.radius.sm]: value.radius.sm,
    [cssVariables.radius.md]: value.radius.md,
    [cssVariables.radius.lg]: value.radius.lg,
    [cssVariables.radius.xl]: value.radius.xl,
    [cssVariables.radius.round]: value.radius.round,
    [cssVariables.font.body]: value.font.body,
    [cssVariables.font.mono]: value.font.mono,
    [cssVariables.fontSize.xs]: value.fontSize.xs,
    [cssVariables.fontSize.sm]: value.fontSize.sm,
    [cssVariables.fontSize.md]: value.fontSize.md,
    [cssVariables.fontSize.lg]: value.fontSize.lg,
    [cssVariables.fontSize.xl]: value.fontSize.xl,
    [cssVariables.fontSize["2xl"]]: value.fontSize["2xl"],
    [cssVariables.fontSize["3xl"]]: value.fontSize["3xl"],
    [cssVariables.fontSize["4xl"]]: value.fontSize["4xl"],
    [cssVariables.fontWeight.regular]: String(value.fontWeight.regular),
    [cssVariables.fontWeight.medium]: String(value.fontWeight.medium),
    [cssVariables.fontWeight.bold]: String(value.fontWeight.bold),
    [cssVariables.fontWeight.black]: String(value.fontWeight.black),
    [cssVariables.lineHeight.tight]: String(value.lineHeight.tight),
    [cssVariables.lineHeight.heading]: String(value.lineHeight.heading),
    [cssVariables.lineHeight.compact]: String(value.lineHeight.compact),
    [cssVariables.lineHeight.body]: String(value.lineHeight.body),
    [cssVariables.shadow.sm]: value.shadow.sm,
    [cssVariables.shadow.md]: value.shadow.md,
    [cssVariables.shadow.lg]: value.shadow.lg,
    [cssVariables.shadow.focus]: value.shadow.focus,
    [cssVariables.duration.instant]: value.duration.instant,
    [cssVariables.duration.fast]: value.duration.fast,
    [cssVariables.duration.normal]: value.duration.normal,
    [cssVariables.duration.page]: value.duration.page,
    [cssVariables.easing.standard]: value.easing.standard,
    [cssVariables.easing.entrance]: value.easing.entrance,
  }
}

export const tokenVar = (variable: `--byfolio-${string}`): string =>
  `var(${variable})`

declare module "@emotion/react" {
  export interface Theme extends ByfolioTheme {
    readonly __byfolioThemeBrand?: true
  }
}
